/**
 * EvidencePackExport — the umbrella that assembles all other schemas into the
 * deliverable handed to OEM/Tier1 audiences. Plan §26.13.
 *
 * Five top-level structural defenses from §25 lessons:
 *   §25.7 — readinessHeader is the 5-way ReadinessHeaderSchema. No aggregate.
 *   §25.8 — lifecycleBoard.columns is `.length(6)` (A/B/C/D/P/U).
 *   §25.10 — audienceSlices.warRoom.actionQueue has NO `assignedTo` /
 *           `dispatchedTo` / `owner` / `dueByOwner` field. `suggestedReviewer`
 *           is the only reviewer-routing field, and it is optional.
 *   §25.11 — continuingObligationsRegister.duties is required (may be empty).
 *   §25.12 — meta.disclaimer = z.literal(EVIDENCE_PACK_DISCLAIMER). UI cannot
 *           override; CI red line #8 (contract-schema) rejects substitutions.
 */

import { z } from "zod";
import {
  RegulatoryDomain,
  LifecycleLayer,
  EVIDENCE_PACK_DISCLAIMER,
} from "./primitives";
import { MarketEntryConfigSchema } from "./vehicle-config";
import { ComplianceRequirementCardSchema } from "./card";
import { LegalReferenceSchema } from "./legal-reference";
import { EvidenceRecordSchema } from "./evidence";
import { ContinuingObligationSchema } from "./continuing-obligation";
import { ReadinessHeaderSchema, CoverageReadinessSchema } from "./readiness";
import { ReviewDecisionSchema } from "./review";
import { InquiryPackSchema } from "./inquiry";

export const AudienceProfile = z.enum([
  "executive",
  "type_approval",
  "supplier",
  "war_room",
  "post_market",
  "audit",
]);
export type AudienceProfile = z.infer<typeof AudienceProfile>;

const ISO8601 = z.string();
const Count = z.number().int().nonnegative();
const Pct = z.number().int().min(0).max(100);

export const EvidencePackMetaSchema = z.object({
  packId: z.string().uuid(),
  packVersion: z.number().int().positive(),
  schemaVersion: z.string(),
  contractVersion: z.string(),
  generatedAt: ISO8601,
  generatedBy: z.object({
    userId: z.string(),
    runId: z.string(),
    mode: z.enum(["interactive", "scheduled", "ci_dryrun"]),
  }),
  registrySnapshotId: z.string().min(1),
  registrySnapshotDate: ISO8601,
  parentPackId: z.string().uuid().nullable(),
  disclaimer: z.literal(EVIDENCE_PACK_DISCLAIMER),
  audienceProfile: AudienceProfile,
  languagePair: z.tuple([z.literal("zh"), z.literal("en")]),
  coreContext: z.object({
    businessUnit: z.string(),
    programName: z.string(),
    programPhase: z.string(),
  }),
});
export type EvidencePackMeta = z.infer<typeof EvidencePackMetaSchema>;

export const ConfigCompletenessSchema = z.object({
  totalFieldsTracked: Count,
  filledFields: Count,
  unknownFieldPaths: z.array(z.string()),
  configCompletenessPercent: Pct,
  r2P0RequiredFieldsStatus: z.array(
    z.object({
      fieldPath: z.string(),
      status: z.enum(["filled", "unknown", "n/a"]),
    }),
  ),
});

export const LifecycleBoardSchema = z.object({
  columns: z
    .array(
      z.object({
        layer: LifecycleLayer,
        title: z.string(),
        cardCount: Count,
        cardIds: z.array(z.string()),
      }),
    )
    .length(6),
  hiddenByConfigCards: z.array(
    z.object({
      cardId: z.string(),
      hiddenByFields: z.array(z.string()),
      regulatoryDomain: RegulatoryDomain,
    }),
  ),
  unknownNeedsConfigCards: z.array(
    z.object({
      cardId: z.string(),
      needsConfigFields: z.array(z.string()),
      regulatoryDomain: RegulatoryDomain,
    }),
  ),
  draftMonitoringCards: z.array(
    z.object({
      cardId: z.string(),
      lifecycleState: z.literal("DRAFT"),
      regulatoryDomain: RegulatoryDomain,
    }),
  ),
  placeholderCurationGapCards: z.array(
    z.object({
      cardId: z.string(),
      lifecycleState: z.literal("PLACEHOLDER"),
      regulatoryDomain: RegulatoryDomain,
    }),
  ),
  byRegulatoryDomain: z.record(RegulatoryDomain, Count).optional(),
  hiddenByRegulatoryDomain: z.record(RegulatoryDomain, Count).optional(),
  unknownByRegulatoryDomain: z.record(RegulatoryDomain, Count).optional(),
});

export const LegalReferenceAppendixEntrySchema = LegalReferenceSchema.extend({
  refId: z.string().min(1),
  citedByCardIds: z.array(z.string()),
});

export const ContinuingObligationsRegisterSchema = z.object({
  duties: z.array(
    z
      .object({
        cardId: z.string(),
        obligationTiming: z.enum([
          "post_market",
          "recurring",
          "event_triggered",
          "phase_in",
        ]),
      })
      .merge(ContinuingObligationSchema.partial())
      .extend({
        jurisdictionCode: z.string(),
        regulatoryDomain: RegulatoryDomain,
      }),
  ),
  byCategory: z.object({
    recallSafety: Count,
    otaSums: Count,
    csms: Count,
    batteryPassport: Count,
    rmiAftermarket: Count,
    memberStateInUse: Count,
    dataLifecycle: Count,
  }),
});

export const SnapshotDiffSchema = z.object({
  parentPackId: z.string(),
  parentRegistrySnapshotDate: ISO8601,
  added: z.array(z.object({ cardId: z.string(), reason: z.string() })),
  removed: z.array(z.object({ cardId: z.string(), reason: z.string() })),
  changed: z.array(
    z.object({
      cardId: z.string(),
      fieldPath: z.string(),
      before: z.unknown(),
      after: z.unknown(),
    }),
  ),
  readinessDelta: z.object({
    legal: z.object({
      before: z.number(),
      after: z.number(),
      delta: z.number(),
    }),
    evidenceStandard: z.object({
      before: z.number(),
      after: z.number(),
      delta: z.number(),
    }),
    marketForce: z.object({
      before: z.number(),
      after: z.number(),
      delta: z.number(),
    }),
    ecosystem: z.object({
      before: z.number(),
      after: z.number(),
      delta: z.number(),
    }),
    continuingObligation: z.object({
      before: z.number(),
      after: z.number(),
      delta: z.number(),
    }),
    activeBindingLegal: z.object({
      before: z.number(),
      after: z.number(),
      delta: z.number(),
    }),
  }),
  legalReferenceChanges: z.object({
    addedRefs: z.array(z.string()),
    removedRefs: z.array(z.string()),
    granularityUpgrades: z.array(
      z.object({
        refId: z.string(),
        fromGranularity: z.string(),
        toGranularity: z.string(),
      }),
    ),
  }),
});

export const AudienceSlicesSchema = z.object({
  executive: z.object({
    top5KeyMessages: z
      .array(z.object({ zh: z.string(), en: z.string() }))
      .length(5),
    headlineMetrics: z.object({
      legalReadinessActiveBindingPercent: Pct,
      submissionBlockingGapCount: Count,
      continuingObligationCoveragePercent: Pct,
      unknownConfigFieldCount: Count,
    }),
    riskFlags: z.array(
      z.object({
        severity: z.enum(["high", "medium", "info"]),
        cardId: z.string(),
        summary: z.object({ zh: z.string(), en: z.string() }),
      }),
    ),
    recommendedDecisions: z.array(
      z.object({
        decisionId: z.string(),
        summary: z.object({ zh: z.string(), en: z.string() }),
        requiredBy: ISO8601,
      }),
    ),
  }),
  typeApproval: z.object({
    byJurisdiction: z.record(
      z.string(),
      z.object({
        cardIds: z.array(z.string()),
        submissionBlockingGapCount: Count,
        evidenceChecklist: z.array(
          z.object({
            evidenceRequirementDescription: z.string(),
            providedRefIds: z.array(z.string()),
            gap: z.boolean(),
          }),
        ),
        taaContact: z
          .object({
            authority: z.string(),
            contactProtocol: z.string(),
          })
          .optional(),
      }),
    ),
  }),
  supplier: z.object({
    bySupplierScope: z.record(
      z.string(),
      z.object({
        cardIds: z.array(z.string()),
        deliveryAsks: z.array(
          z.object({
            ask: z.object({ zh: z.string(), en: z.string() }),
            dueBy: ISO8601,
            evidenceRequirementDescription: z.string(),
          }),
        ),
      }),
    ),
  }),
  warRoom: z.object({
    weekDelta: z.object({
      weekStart: ISO8601,
      weekEnd: ISO8601,
      addedCount: Count,
      removedCount: Count,
      changedCount: Count,
    }),
    actionQueue: z.array(
      z.object({
        cardId: z.string(),
        proposedAction: z.object({ zh: z.string(), en: z.string() }),
        suggestedReviewer: z.string().email().optional(),
        severity: z.enum(["high", "medium", "info"]),
      }),
    ),
  }),
  postMarket: z.object({
    upcomingDuties: z.array(
      z.object({
        dutyCardId: z.string(),
        nextDueWindow: z.string(),
        daysUntilDue: z.number().int(),
      }),
    ),
    activeMonitoringSignals: z.array(
      z.object({
        signalSource: z.string(),
        dutyCardIds: z.array(z.string()),
        lastObservedAt: ISO8601,
      }),
    ),
  }),
  audit: z.object({
    evidenceRefIds: z.array(z.string()),
    reviewerDecisionIds: z.array(z.string()),
    provenanceLedger: z.array(
      z.object({
        sourceRepo: z.string(),
        commit: z.string(),
        snapshotDate: ISO8601,
        bundleId: z.string().optional(),
      }),
    ),
    canonicalSnapshotHash: z.string(),
  }),
});

export const EvidencePackExportSchema = z.object({
  meta: EvidencePackMetaSchema,
  vehicleProfile: MarketEntryConfigSchema,
  configCompleteness: ConfigCompletenessSchema,
  readinessHeader: ReadinessHeaderSchema,
  coverageReadiness: CoverageReadinessSchema,
  lifecycleBoard: LifecycleBoardSchema,
  obligations: z.array(ComplianceRequirementCardSchema),
  legalReferencesAppendix: z.object({
    references: z.array(LegalReferenceAppendixEntrySchema),
  }),
  evidenceInventory: z.object({
    evidenceRecords: z.array(EvidenceRecordSchema),
  }),
  continuingObligationsRegister: ContinuingObligationsRegisterSchema,
  inquiryHypothesisLog: z.object({
    inquiries: z.array(InquiryPackSchema),
  }),
  reviewerTrail: z.object({
    decisions: z.array(ReviewDecisionSchema),
  }),
  snapshotDiff: SnapshotDiffSchema.optional(),
  audienceSlices: AudienceSlicesSchema,
  i18n: z.object({
    zh: z.object({
      sectionLabels: z.record(z.string(), z.string()),
      narrativeBlocks: z.record(z.string(), z.string()),
    }),
    en: z.object({
      sectionLabels: z.record(z.string(), z.string()),
      narrativeBlocks: z.record(z.string(), z.string()),
    }),
  }),
});
export type EvidencePackExport = z.infer<typeof EvidencePackExportSchema>;
